<script setup>
import {computed} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useUserStore} from '@/stores/user'
import {useModalsStore} from '@/stores/modals'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'
import {useSiteTreeStore} from '@/stores/siteTree'
import {useAppStore} from '@/stores/appStore'
import FormSidebarBtn from "./FormSidebarBtn.vue"

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
})

const formStore = useFormStore(props.formId)
const componentsStore = useComponentsStore()
const userStore = useUserStore()
const modalsStore = useModalsStore()
const itemDataStore = useItemDataStore()
const siteTreeStore = useSiteTreeStore('siteTree')
const appStore = useAppStore()

const isArchived = computed(() => !!formStore.currentData?.isArchived)
const buttonText = computed(() => isArchived.value ? 'Remove from archive' : 'Add to archive')
const tableName = computed(() => formStore.tableName)
const id = computed(() => formStore.id)
const componentId = computed(() => componentsStore.componentIds[tableName.value])
const hasAccess = computed(() => userStore.access(tableName.value, 'archive'))

const show = computed(() => {
    if (!id.value) {
        return false
    }

    // The home page cannot be archived
    if (tableName.value === 'pages' &&  id.value === appStore.settings.homePage) {
        return false
    }

    // Content should not be archived from the site tree, as this removes the content from all pages.
    // Users must archive it explicitly from the Content component.
    if (
        tableName.value === 'content' && window.location.hash.split('/')[1] === 'site_tree'
    ) {
        return false
    }

    if (hasAccess.value) {
        const component = componentsStore.items.find(obj => obj.id === componentId.value)
        return !!component?.useArchive
    }

    return false
})

// --- Methods ---

const onClick = () => {
    const modalTitle = isArchived.value ? 'Remove from archive' : 'Add to archive'
    const modalActionText = isArchived.value ? 'remove this item from' : 'add this item to'

    modalsStore.show({
        componentName: 'ConfirmModal',
        obj: {
            modalTitle: modalTitle,
            modalBody: `<p>Are you sure you wish to ${modalActionText} the archive?</p>`,
            onConfirm: () => {
                // The isArchived column stores 1|0, not true|false.
                const value = isArchived.value ? 0 : 1

                itemDataStore.archive({
                        tableName: tableName.value,
                        id: id.value,
                        doArchive: value,
                    })
                    .then(() => {
                        formStore.presetData({
                            name: 'isArchived',
                            value: value,
                        })

                        // Refresh site tree pane
                        siteTreeStore.init()
                    })
                    .catch((error) => {
                        // silent catch
                    })
            }
        }
    })
}
</script>

<template>
    <FormSidebarBtn v-if="show" @click.prevent="onClick">
        <i class="me-2 bi-archive"/>
        {{ buttonText }}
    </FormSidebarBtn>
</template>